// src/components/super-admin/CreateEnterpriseModal.js - 기업 계정 생성 모달

import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { getPersonaPresets } from '../../services/api';
import './CreateEnterpriseModal.scss';

const FALLBACK_PRESETS = [
  { id: 'telecom', label: '통신 (Telecom)' },
  { id: 'finance', label: '금융/투자 (Finance)' },
];

const CreateEnterpriseModal = ({ isOpen, onClose, onSubmit, loading = false }) => {
  const [form, setForm] = useState({
    email: '',
    name: '',
    company_name: '',
    department: '',
    personaPresetId: 'telecom',
  });
  const [personaPresets, setPersonaPresets] = useState(FALLBACK_PRESETS);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getPersonaPresets();
        if (!cancelled && data.persona_presets?.length) {
          setPersonaPresets(data.persona_presets);
        }
      } catch (err) {
        console.error('Persona 프리셋 목록 조회 실패:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.email.trim()) newErrors.email = '이메일을 입력해주세요';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = '유효한 이메일을 입력해주세요';
    if (!form.name.trim()) newErrors.name = '이름을 입력해주세요';
    if (!form.company_name.trim()) newErrors.company_name = '기업명을 입력해주세요';
    if (!form.personaPresetId) newErrors.personaPresetId = '도메인을 선택해주세요';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({
      email: form.email.trim(),
      name: form.name.trim(),
      company_name: form.company_name.trim(),
      department: form.department.trim(),
      personaPresetId: form.personaPresetId,
    });
  };

  const handleClose = () => {
    setForm({
      email: '', name: '', company_name: '', department: '', personaPresetId: 'telecom',
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content create-enterprise-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>기업 계정 생성</h3>
          <button className="modal-close-btn" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-section">
              <div className="form-section-title">오너 계정 정보</div>
              <div className="form-group">
                <label className="form-label">이메일 <span className="required">*</span></label>
                <input
                  type="email"
                  className="form-input"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="owner@company.com"
                />
                {errors.email && <div className="form-error">{errors.email}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">이름 <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="홍길동"
                />
                {errors.name && <div className="form-error">{errors.name}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">초기 비밀번호</label>
                <div className="form-input" style={{ background: '#f9fafb', color: '#6b7280', cursor: 'default', userSelect: 'none' }}>
                  0000
                </div>
                <div className="form-hint">초기 비밀번호는 0000으로 자동 설정됩니다. 오너는 첫 로그인 후 비밀번호를 변경해야 합니다.</div>
              </div>
            </div>

            <div className="form-section">
              <div className="form-section-title">회사 정보</div>
              <div className="form-group">
                <label className="form-label">기업명 <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  value={form.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="(주)리옵스"
                />
                {errors.company_name && <div className="form-error">{errors.company_name}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">도메인 프리셋 <span className="required">*</span></label>
                <select
                  className="form-input"
                  value={form.personaPresetId}
                  onChange={(e) => handleChange('personaPresetId', e.target.value)}
                >
                  {personaPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.label || preset.id}
                    </option>
                  ))}
                </select>
                <div className="form-hint">퍼소나 생성·인터뷰 pack에 사용할 정식 도메인입니다. 교통/카드/보험/생활 포함. 생성 후에도 기업 상세에서 변경할 수 있습니다.</div>
                {errors.personaPresetId && <div className="form-error">{errors.personaPresetId}</div>}
              </div>
              <div className="form-group">
                <label className="form-label">소속 부서</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  placeholder="Research"
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={handleClose}>
              취소
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? '생성 중...' : '기업 계정 생성'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEnterpriseModal;
